import React from "react";
import { ArrowRight, Phone, ChevronDown } from "lucide-react";
import { useLang } from "../lib/LangContext";
import { TID } from "../lib/testIds";

const FLYER_URL =
  "https://customer-assets.emergentagent.com/job_transport-reinigung-1/artifacts/5000woal_Semi.jpeg";

export default function Hero() {
  const { t } = useLang();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const top = el.getBoundingClientRect().top + window.scrollY - 72;
      window.scrollTo({ top, behavior: "smooth" });
    }
  };

  return (
    <section
      id="home"
      data-testid={TID.heroSection}
      className="relative min-h-screen flex items-center overflow-hidden bg-[#0A0A0A] grain"
    >
      {/* Backdrop pattern */}
      <div className="absolute inset-0 opacity-[0.07]" aria-hidden="true">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, rgba(212,175,55,0.35) 0%, transparent 35%), radial-gradient(circle at 80% 70%, rgba(227,0,15,0.18) 0%, transparent 30%)",
          }}
        />
      </div>

      {/* Vertical side label */}
      <div className="hidden md:block absolute left-6 top-1/2 -translate-y-1/2 -rotate-90 origin-left text-[10px] tracking-[0.4em] text-white/40">
        ZEMI · SWISS · GMBH · CH-9100 HERISAU
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 pb-20 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left: text */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-3 px-3 py-1.5 border border-[#D4AF37]/30 bg-[#D4AF37]/5">
              <span className="w-1.5 h-1.5 bg-[#E3000F] rounded-full animate-pulse" />
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-[#D4AF37]">
                {t.hero.eyebrow}
              </span>
            </div>

            <h1
              data-testid={TID.heroTitle}
              className="font-display text-white text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.95] tracking-tight mt-6"
            >
              ZEMI
              <span className="block text-[#D4AF37]">SWISS</span>
              <span className="block text-white/80 text-3xl sm:text-4xl lg:text-5xl mt-2 font-normal">GmbH</span>
            </h1>

            <p className="mt-8 text-lg sm:text-xl text-white/90 max-w-xl font-light leading-relaxed">
              {t.hero.subtitle}
            </p>
            <p className="mt-4 text-base text-white/60 max-w-xl leading-relaxed">
              {t.hero.description}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <button
                type="button"
                onClick={() => scrollTo("quote")}
                data-testid={TID.heroCtaQuote}
                className="group inline-flex items-center justify-center gap-2 bg-[#D4AF37] hover:bg-[#C5A028] text-black font-semibold px-8 py-4 transition-all duration-300"
              >
                <span>{t.hero.ctaPrimary}</span>
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href="tel:+41783215249"
                data-testid={TID.heroCtaCall}
                className="group inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white hover:bg-white hover:text-black font-semibold px-8 py-4 transition-all duration-300"
              >
                <Phone className="w-4 h-4" />
                <span>{t.hero.ctaSecondary}</span>
              </a>
            </div>

            {/* Phone numbers under hero */}
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-2 text-sm text-white/60">
              <a href="tel:+41783215249" className="gold-link hover:text-white">+41 78 321 52 49</a>
              <span className="opacity-30">|</span>
              <a href="tel:+41782584261" className="gold-link hover:text-white">+41 78 258 42 61</a>
              <span className="opacity-30">|</span>
              <a href="mailto:info@zemiswiss.ch" className="gold-link hover:text-white">info@zemiswiss.ch</a>
            </div>
          </div>

          {/* Right: flyer card */}
          <div className="lg:col-span-5 hidden lg:flex justify-center">
            <div className="relative">
              <div className="absolute -inset-8 bg-[#D4AF37]/10 blur-3xl rounded-full" aria-hidden="true" />
              <div className="relative bg-black border border-[#D4AF37]/40 shadow-2xl overflow-hidden">
                <img
                  src={FLYER_URL}
                  alt="ZEMI SWISS GmbH Visitenkarte"
                  className="w-full max-w-md h-auto select-none block"
                  draggable={false}
                />
                <div className="absolute -top-3 -right-3 bg-[#E3000F] text-white px-3 py-1 text-[10px] font-bold tracking-[0.3em] uppercase">
                  Swiss · CH
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll hint */}
        <button
          type="button"
          onClick={() => scrollTo("about")}
          className="absolute left-1/2 -translate-x-1/2 bottom-6 sm:bottom-10 inline-flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors"
          aria-label={t.hero.scrollHint}
          data-testid="hero-scroll-hint"
        >
          <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scrollHint}</span>
          <ChevronDown className="w-5 h-5 animate-bounce" strokeWidth={1.5} />
        </button>
      </div>
    </section>
  );
}
